import React from 'react';
import { Tooltip, IconButton } from '@mui/material';
import { ShoppingCart as ShoppingCartIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import config from '../../config/config.json';
import { notifyCartItemAdded } from '../../utils/cartUtils';

interface AddToCartActionProps {
  item: any;
  sessionToken?: string;
  size?: 'small' | 'medium' | 'large';
  sx?: any;
}

export default function AddToCartAction({
  item,
  sessionToken,
  size = 'small',
  sx = {},
}: AddToCartActionProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const handleAddToCart = async () => {
    if (!sessionToken) {
      toast.error(t('search.vendor.loginRequired', 'Please log in to add items to cart'));
      return;
    }

    if (isNaN(item.id)) {
      toast.error(t('search.errors.invalidId', 'Invalid item ID'));
      return;
    }

    const url = config.backend_base_url + 'd2rs/cart/add';
    const loading = toast.loading(t('search.cart.adding', 'Adding item to cart'));

    try {
      const response = await axios.post(
        url,
        {
          item_ids: [item.id],
        },
        { headers: { 'session-token': sessionToken } }
      );

      if (response.status) {
        toast.dismiss(loading);
        toast.success(t('search.cart.success', 'Successfully added item to cart'));

        // Notify components about cart update
        queryClient.invalidateQueries({ queryKey: ['cartItems'] });
        notifyCartItemAdded({ id: item.id });
      }
    } catch (error) {
      toast.dismiss(loading);
      toast.error(t('search.errors.cartAddFailed', 'Failed to add item to cart'));
    }
  };

  return (
    <Tooltip
      title={item.status === 'in_cart' ? t('search.cart.takeFromCart') : t('search.cart.addToCart')}
    >
      <IconButton
        onClick={handleAddToCart}
        size={size}
        sx={{
          color: (theme) => theme.palette.text.secondary,
          border: (theme) => `1px solid ${theme.palette.secondary.main}33`,
          '&:hover': {
            color: (theme) => theme.palette.text.primary,
            border: (theme) => `1px solid ${theme.palette.secondary.main}`,
          },
          ...sx,
        }}
      >
        <ShoppingCartIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
}
